import React, { useState, useEffect, useRef } from 'react';
import { Play, Film, ShieldAlert, ArrowLeft, Volume2, VolumeX, MessageSquare, Eye, Heart, ChevronDown, ChevronUp } from 'lucide-react';
import WatchedButton from '../tabs/WatchedButton';
import { fetchTmdbMovieDetails } from '../../services/tmdbService';
import { useVideoMetrics } from '../../services/useVideoMetrics';

const extractYouTubeId = (urlOrId?: string): string => {
  if (!urlOrId) return '';
  const trimmed = String(urlOrId).trim();
  if (trimmed.length === 11 && !trimmed.includes('/') && !trimmed.includes('.')) return trimmed;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = trimmed.match(regExp);
  return (match && match[2].length === 11) ? match[2] : '';
};

const formatCount = (count?: number) => {
  if (count === undefined || count === null || isNaN(count)) return '0';
  const num = Math.floor(count);
  if (num < 0) return '0';
  if (num >= 1000000000) return (num / 1000000000).toFixed(1).replace('.', ',') + 'B';
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace('.', ',') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace('.', ',') + ' mil';
  return num.toString();
};

// Componente para exibir o carrossel do Elenco do TMDB
const CastCarousel = ({ cast, loading }: { cast: any[], loading: boolean }) => {
  if (loading) return <p style={{ color: '#888', fontSize: '12px' }}>Carregando elenco...</p>;
  if (!cast || cast.length === 0) return null;

  return (
    <div style={{ marginTop: '15px' }}>
      <h4 style={{ color: '#fff', fontSize: '13px', margin: '0 0 10px 0', fontWeight: 'bold' }}>🎬 Elenco Principal</h4>
      <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px', scrollbarWidth: 'thin' }}>
        {cast.map((actor) => (
          <div key={actor.id} style={{ minWidth: '70px', maxWidth: '70px', textAlign: 'center' }}>
            {actor.profile_path ? (
              <img 
                src={`https://image.tmdb.org/t/p/w185${actor.profile_path}`} 
                alt={actor.name} 
                style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #444', margin: '0 auto 4px auto' }} 
              />
            ) : (
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#222', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 4px auto', color: '#666', fontSize: '10px' }}>Sem foto</div>
            )}
            <p style={{ color: '#fff', fontSize: '11px', margin: 0, fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{actor.name}</p>
            <p style={{ color: '#888', fontSize: '9px', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{actor.character}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function DetailsHero({ video, isStandaloneContent, primaryLink, trailerLink, onBack, onPlayMovie, onPlayTrailer }: any) {
  const [isMuted, setIsMuted] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showVideo, setShowVideo] = useState(false);
  const [showDetailsBox, setShowDetailsBox] = useState(false);
  const [audioChoice, setAudioChoice] = useState(video.audio_type || 'DUBLADO');

  // 🚀 Utiliza o Custom Hook isolado para as métricas do Supabase
  const { likes, hasLiked, views, commentsCount, handleLike } = useVideoMetrics(video.id, video.views_count, video.likes_count);

  // 🎬 Utiliza o Serviço Isolado do TMDB
  const [cast, setCast] = useState<any[]>([]);
  const [runtime, setRuntime] = useState<number>(0);
  const [loadingCast, setLoadingCast] = useState(false);

  const desktopIframeRef = useRef<HTMLIFrameElement>(null);
  const mobileIframeRef = useRef<HTMLIFrameElement>(null);

  // 🚀 Ajuste cirúrgico: Acorda o player do telemóvel ao voltar do WhatsApp/outros apps
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const activeIframe = isMobile ? mobileIframeRef.current : desktopIframeRef.current;
        if (activeIframe && activeIframe.contentWindow) {
          activeIframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
            '*'
          );
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isMobile]);

  useEffect(() => {
    const loadTmdb = async () => {
      if (!video?.tmdb_id) return;
      setLoadingCast(true);
      const data = await fetchTmdbMovieDetails(video.tmdb_id);
      setCast(data.cast);
      setRuntime(data.runtime);
      setLoadingCast(false);
    };
    loadTmdb();
  }, [video?.tmdb_id]);

  useEffect(() => {
    setShowVideo(false);
    const timer = setTimeout(() => setShowVideo(true), 5000); 
    return () => clearTimeout(timer);
  }, [video?.id]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMute = () => {
    const command = isMuted ? 'unMute' : 'mute';
    const activeIframe = isMobile ? mobileIframeRef.current : desktopIframeRef.current;
    if (activeIframe && activeIframe.contentWindow) {
      activeIframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: command, args: [] }),
        '*'
      );
    }
    setIsMuted(!isMuted);
  };

  const activeTrailerUrl = trailerLink || primaryLink;
  const ytId = extractYouTubeId(activeTrailerUrl);
  
  const rawText = video.description || video.sinpose || video.sinopse || 'Acompanhe este conteúdo incrível em alta definição no Doisplay.';
  const cleanedText = rawText.replace(/3\s*Curiosidades[:\s]*/gi, '');
  const textParts = cleanedText.split(/(?=\b[1-3]\.\s|🔥)/);
  const mainSynopsis = textParts[0].trim(); 

  const parseFormattedDescription = (raw: string) => {
    if (!raw) return { synopsis: '', sections: [] };
    let cleanText = raw.replace(/^sinopse:\s*/i, '');
    const keywords = ['Curiosidades', 'Inspiração', 'Resgate', 'Linhagem', 'Detalhes', 'Elenco', 'Direção'];
    let synopsisPart = cleanText;
    let restPart = '';

    for (const kw of keywords) {
      const index = cleanText.indexOf(kw);
      if (index !== -1 && index > 50) {
        synopsisPart = cleanText.substring(0, index).trim();
        restPart = cleanText.substring(index).trim();
        break;
      }
    }
    const sections = restPart ? restPart.split(/(?=[A-Z][A-Za-zÀ-ÿ\s]+:\s)/) : [];
    return { synopsis: synopsisPart, sections };
  };

  const Metrics = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginTop: '16px', color: '#bbb', fontSize: '14px', fontWeight: '600', justifyContent: isMobile ? 'center' : 'flex-start', flexWrap: 'wrap' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Eye size={18} color="#aaa" /> {formatCount(views)}</span>
      <span onClick={handleLike} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: hasLiked ? '#E50914' : '#bbb', transition: 'color 0.2s' }}>
        <Heart size={18} color="#E50914" fill={hasLiked ? '#E50914' : 'none'} /> {formatCount(likes)}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><MessageSquare size={18} color="#3b82f6" /> {formatCount(commentsCount)}</span>
      {runtime > 0 && (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#aaa' }}>
          ⏱️ {runtime} min
        </span>
      )}
    </div>
  );

  const DetailsBox = () => {
    if (!showDetailsBox) return null;
    const { synopsis, sections } = parseFormattedDescription(rawText);

    return (
      <div style={{ marginTop: '20px', backgroundColor: '#111', border: '1px solid #333', borderRadius: '8px', padding: '20px', animation: 'fadeIn 0.3s ease-out' }}>
        <h3 style={{ color: '#fff', fontSize: '16px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📖 Sinopse Completa e Detalhes
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', color: '#ccc', fontSize: '13px', lineHeight: '1.6' }}>
          <div><p style={{ margin: 0, color: '#ddd' }}>{synopsis}</p></div>
          {sections.length > 0 && sections.map((sec, idx) => {
            const parts = sec.split(':');
            const title = parts.length > 1 ? parts[0] : `Detalhe ${idx + 1}`;
            const content = parts.length > 1 ? parts.slice(1).join(':') : sec;
            return (
              <div key={idx} style={{ backgroundColor: 'rgba(255,255,255,0.04)', padding: '10px 12px', borderRadius: '6px', borderLeft: '3px solid #E50914' }}>
                <h5 style={{ color: '#fff', fontSize: '12px', margin: '0 0 4px 0', fontWeight: 'bold' }}>💡 {title}</h5>
                <p style={{ margin: 0, color: '#aaa', fontSize: '12px' }}>{content.trim()}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (isMobile) {
    return (
      <div style={{ position: 'relative', width: '100%', backgroundColor: '#000000', paddingBottom: '30px' }}>
        <button onClick={onBack} style={{ position: 'absolute', top: '15px', left: '15px', zIndex: 40, display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(0,0,0,0.7)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
          <ArrowLeft size={14} /> Voltar
        </button>

        <div style={{ position: 'relative', width: '100%', height: '240px', backgroundColor: '#111' }}>
          {ytId && showVideo ? (
            <iframe 
              ref={mobileIframeRef}
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&controls=0&showinfo=0&rel=0&loop=1&playlist=${ytId}&end=60&modestbranding=1&enablejsapi=1`} 
              style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }} 
              allow="autoplay; encrypted-media" 
              title="Details Trailer Mobile" 
            />
          ) : (
            <img src={video.thumbnail} alt={video.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
          
          {ytId && showVideo && (
            <button onClick={toggleMute} style={{ position: 'absolute', bottom: '12px', right: '12px', zIndex: 30, backgroundColor: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', pointerEvents: 'auto' }}>
              {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
          )}
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ backgroundColor: '#E50914', color: '#fff', fontSize: '10px', padding: '3px 8px', borderRadius: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>{video.category?.split(':')[1] || video.category || 'Filme'}</span>
            <span 
              onClick={() => setAudioChoice((a: string) => a === 'DUBLADO' ? 'LEGENDADO' : 'DUBLADO')}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', fontSize: '10px', padding: '2px 8px', borderRadius: '3px', fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
            >
              {audioChoice}
            </span>
            {video.is_premium && <span style={{ display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: '#ffd700', color: '#000', fontSize: '10px', padding: '3px 8px', borderRadius: '3px', fontWeight: 'bold' }}><ShieldAlert size={12} /> VIP</span>}
          </div>

          <h1 style={{ fontSize: '24px', fontWeight: '900', color: '#fff', margin: '0 0 12px 0', lineHeight: '1.2' }}>{video.title}</h1>
          
          <p style={{ fontSize: '13px', color: '#ccc', lineHeight: '1.5', margin: '0 0 10px 0', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {mainSynopsis}
          </p>
          
          {rawText.length > 150 && (
            <button 
              onClick={() => setShowDetailsBox(!showDetailsBox)}
              style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '12px', fontWeight: 'bold', padding: 0, marginBottom: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              {showDetailsBox ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showDetailsBox ? 'Ocultar Detalhes' : 'Ler mais curiosidades'}
            </button>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {isStandaloneContent ? (
              <>
                <button onClick={onPlayMovie} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: '#E50914', color: '#fff', border: 'none', padding: '14px', borderRadius: '4px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}><Play size={18} fill="#fff" /> Assistir Vídeo</button>
                <WatchedButton videoId={video.id} />
              </>
            ) : (
              <>
                <button onClick={onPlayMovie} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: '#E50914', color: '#fff', border: 'none', padding: '14px', borderRadius: '4px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}><Play size={18} fill="#fff" /> Assistir Completo</button>
                {trailerLink && <button onClick={onPlayTrailer} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', padding: '12px', borderRadius: '4px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}><Film size={18} /> Trailer Oficial</button>}
                <WatchedButton videoId={video.id} />
              </>
            )}
          </div>
          
          <Metrics />
          <DetailsBox />
          {showDetailsBox && <CastCarousel cast={cast} loading={loadingCast} />}
        </div>
      </div>
    );
  }

  // DESKTOP
  return (
    <div style={{ position: 'relative', width: '100%', minHeight: '70vh', backgroundColor: '#000000', overflow: 'hidden', paddingBottom: showDetailsBox ? '60px' : '0' }}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div style={{ position: 'absolute', top: '25px', left: '40px', zIndex: 30 }}>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(0,0,0,0.7)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}><ArrowLeft size={16} /> Voltar</button>
      </div>

      {ytId && showVideo ? (
        <div style={{ position: 'absolute', top: '-15%', right: '-5%', width: '75%', height: '130%', pointerEvents: 'none' }}>
          <iframe 
            ref={desktopIframeRef}
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&controls=0&showinfo=0&rel=0&loop=1&playlist=${ytId}&modestbranding=1&enablejsapi=1`} 
            style={{ width: '100%', height: '100%', border: 'none' }} 
            allow="autoplay; encrypted-media" 
            title="Details Trailer Desktop" 
          />
        </div>
      ) : (
        <img src={video.thumbnail} alt={video.title} style={{ position: 'absolute', top: 0, right: 0, width: '65%', height: '100%', objectFit: 'cover' }} />
      )}

      {ytId && showVideo && (
        <button onClick={toggleMute} style={{ position: 'absolute', top: '25px', right: '35px', zIndex: 30, backgroundColor: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', pointerEvents: 'auto' }}>
          {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      )}

      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: `linear-gradient(to right, #000000 45%, rgba(0,0,0,0.85) 60%, rgba(0,0,0,0.2) 80%, transparent 100%), linear-gradient(to top, #000000 0%, transparent 20%)`, pointerEvents: 'none' }} />

      <div style={{ position: 'relative', width: '100%', minHeight: '100%', display: 'flex', alignItems: 'center', padding: '80px 60px 40px 60px', boxSizing: 'border-box', zIndex: 10 }}>
        <div style={{ maxWidth: '600px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <span style={{ backgroundColor: '#E50914', color: '#fff', fontSize: '11px', padding: '3px 8px', borderRadius: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>{video.category?.split(':')[1] || video.category || 'Filme'}</span>
            <span 
              onClick={() => setAudioChoice((a: string) => a === 'DUBLADO' ? 'LEGENDADO' : 'DUBLADO')}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', fontSize: '11px', padding: '2px 8px', borderRadius: '3px', fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
            >
              {audioChoice}
            </span>
            {video.is_premium && <span style={{ display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: '#ffd700', color: '#000', fontSize: '11px', padding: '3px 8px', borderRadius: '3px', fontWeight: 'bold' }}><ShieldAlert size={12} /> VIP</span>}
          </div>
          
          <h1 style={{ fontSize: 'clamp(30px, 3.5vw, 42px)', fontWeight: '900', color: '#fff', margin: '0 0 15px 0', lineHeight: '1.15' }}>{video.title}</h1>
          
          <p style={{ fontSize: '14px', color: '#ccc', lineHeight: '1.5', margin: '0 0 10px 0', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {mainSynopsis}
          </p>

          {rawText.length > 150 && (
            <button 
              onClick={() => setShowDetailsBox(!showDetailsBox)}
              style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '13px', fontWeight: 'bold', padding: 0, marginBottom: '25px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', transition: 'color 0.2s' }}
            >
              {showDetailsBox ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {showDetailsBox ? 'Ocultar Detalhes' : 'Ler mais curiosidades'}
            </button>
          )}

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {isStandaloneContent ? (
              <>
                <button onClick={onPlayMovie} style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#E50914', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: '4px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}><Play size={18} fill="#fff" /> Assistir Vídeo</button>
                <WatchedButton videoId={video.id} />
              </>
            ) : (
              <>
                <button onClick={onPlayMovie} style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#E50914', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: '4px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}><Play size= {18} fill="#fff" /> Assistir Completo</button>
                {trailerLink && <button onClick={onPlayTrailer} style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', padding: '12px 24px', borderRadius: '4px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}><Film size={18} /> Trailer</button>}
                <WatchedButton videoId={video.id} />
              </>
            )}
          </div>
          
          <Metrics />
          <DetailsBox />
          
          {showDetailsBox && <div style={{ marginTop: '15px', maxWidth: '600px' }}><CastCarousel cast={cast} loading={loadingCast} /></div>}
        </div>
      </div>
    </div>
  );
}